import React, {
	Suspense,
	useDeferredValue,
	useEffect,
	useMemo,
	useRef,
	useState,
	useTransition,
} from 'react';
import HybridDeploymentMgr from 'hybridDeployment/HybridDeploymentMgr';

import ComponentErrorBoundary from 'coreComponents/ComponentErrorBoundary';
import LoaderComponent from 'coreComponents/LoaderComponent';
import ScrollRestoration from 'coreComponents/ScrollRestoration';
import {useRouterTransitionPending} from 'coreComponents/TransitionalRouter/TransitionPendingContext';
import {markServerDataHydrated, useIsFirstRender, useSetUI, useSSRRedirect, useSSRRequest} from 'coreHooks';
import {NavigationContext} from 'coreHooks/NavigationContext';
import useIsomorphicLayoutEffect from 'coreHooks/useIsomorphicLayoutEffect';
import {selectRoute} from 'coreStore/data/page';
import {isKeepAliveSupported, KeepAlive} from 'coreUtils/adapters/keepAlive';
import {suspenseComponent} from 'coreUtils/adapters/lazy';
import {useRouterHistory} from 'coreUtils/adapters/router';
import {safeDecodeURIComponent} from 'coreUtils/url';
import {isServer} from 'coreUtils/utils';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import {getKeepAliveRoutes, isKeepAliveRoute, navigateSlots, NavigationAction, PageSlot, PageSlots} from './pageSlots';
import RouteManager from './RouteManager';

const PageNotFound = suspenseComponent(/* #__LOADABLE__ */ () => import('brand/app/pages/page-not-found'));
// Root of a locale without the trailing slash (`/it-it`): the server redirects it to `/it-it/` (301).
const LOCALE_ROOT_RE = /^\/[a-z]{2}-[a-z]{2}$/i;

const Effects = ({location, onExternalRedirect}) => {
	useEffect(() => {
		HybridDeploymentMgr.handleLocation(location, onExternalRedirect);
	}, [location]);

	return null;
};

const StrictLocaleLoaderRouter = ({location: incomingLocation}) => {
	const isTransitionPending = useRouterTransitionPending();
	const location = useDeferredValue(incomingLocation);
	const history = useRouterHistory();
	const navigationAction = useDeferredValue(history.action as NavigationAction);
	// Tracks the transition that swaps the page: it is the one suspending on the new page chunk/data.
	const [isRoutePending, startRouteTransition] = useTransition();
	const req = useSSRRequest();
	const isPending = isTransitionPending || isRoutePending || incomingLocation !== location;
	const {managerRepository, setIsAppLoading} = useGeneralProvider();
	const [isRedirectingToExternalHost, setExternalRedirectStatus] = useState(false);
	const countriesMgr = managerRepository.getCountriesMgr();
	const setUI = useSetUI();
	const isFirstRender = useIsFirstRender();
	const redirect = useSSRRedirect();
	const [routeChanged, triggerRouteChange] = useState(false);
	const [scroll, triggerScroll] = useState(false);
	const currentRoute = useMemo(
		() => RouteManager.getRoute(safeDecodeURIComponent(location.pathname)),
		[location.pathname],
	);
	const routeKey = useMemo(() => `${location.pathname}${location.search}`, [location.search, location.pathname]);
	// Page component of the URL already in the router (the deferred `location` lags behind it).
	const incomingComponent = useMemo(
		() => RouteManager.getRoute(safeDecodeURIComponent(incomingLocation.pathname))?.component,
		[incomingLocation.pathname],
	);
	const prevNavRef = useRef({location, currentRoute});
	// On the client no route matches it either (the brand tables want `/:locale/...`): same redirect as the server.
	const localeRootRedirect =
		!incomingComponent && LOCALE_ROOT_RE.test(incomingLocation.pathname)
			? `${incomingLocation.pathname}/${incomingLocation.search}${incomingLocation.hash}`
			: null;

	useIsomorphicLayoutEffect(() => {
		if (localeRootRedirect) {
			history.replace(localeRootRedirect);
		}
	}, [localeRootRedirect]);

	// Locale validation
	const locale = currentRoute?.params?.locale;
	const [languageCode, countryCode] = useMemo(() => (locale ? locale?.split('-') : []), [locale]);
	const detectedCountry = useMemo(
		() => countryCode && countriesMgr.getCountryByCode(countryCode?.toUpperCase()),
		[countryCode],
	);
	const isLocaleCorrect = useMemo(
		() =>
			!locale ||
			(detectedCountry &&
				(!detectedCountry.hasAvailableLanguages() || detectedCountry.isLanguageAvailable(languageCode))),
		[detectedCountry, locale, languageCode],
	);
	const [slots, setSlots] = useState<PageSlots>(() => ({
		current: {id: 0, Component: isLocaleCorrect ? currentRoute?.component : PageNotFound, location, locale},
	}));

	// SSR locale redirect (URL needs locale prefix adjustment)
	if (isLocaleCorrect && isServer) {
		const originalUrl = req.originalUrl;
		const locationPathnameWithLocale = managerRepository.getLocalesMgr().updateLinkWithLocale(originalUrl);

		const compare1 = new URL(originalUrl, 'https://www.example.com');
		const compare2 = new URL(locationPathnameWithLocale, 'https://www.example.com');

		if (
			compare1.pathname !== compare2.pathname ||
			compare1.hash !== compare2.hash ||
			compare1.searchParams.toString() !== compare2.searchParams.toString()
		) {
			redirect(301, locationPathnameWithLocale);
		}
	}

	// Set app loading state synchronously after commit (before paint) so that
	// the useRoute hook's freeze mechanism is active before any concurrent
	// render can dispatch receiveRoute to Redux.
	useIsomorphicLayoutEffect(() => {
		if (isRedirectingToExternalHost) {
			return;
		}

		if (isPending) {
			setIsAppLoading(true);
		} else if (!isFirstRender) {
			setIsAppLoading(false);
		}
	}, [isPending, routeKey]);

	// Spinner + scroll restoration trigger can remain async (visual-only).
	useEffect(() => {
		if (isRedirectingToExternalHost) {
			return;
		}

		if (isPending) {
			managerRepository.getSpinnerMgr().show(true);
		} else if (!isFirstRender) {
			managerRepository.getSpinnerMgr().show(false);
			setUI.setNavigatingToCorrectLocale(false);
		}
	}, [isPending, routeKey]);

	useIsomorphicLayoutEffect(() => {
		if (isFirstRender) {
			return;
		}

		// Safety net for pages not wrapped in `withSSRPage`: the next page must not reuse hydration rules.
		markServerDataHydrated();

		const previous = prevNavRef.current;
		const locationChanged = previous.location !== location;
		const routeChanged = previous.currentRoute !== currentRoute;
		// Pages kept alive (`app.keepAlive`) only where a hidden page stays mounted (`<Activity>`). The store
		// route is the one of the page on screen: the next page has not rendered yet.
		const keepAlive =
			isKeepAliveSupported &&
			isKeepAliveRoute(selectRoute(managerRepository.getStoreMgr().getStore().getState()).currentRoute, getKeepAliveRoutes());

		prevNavRef.current = {location, currentRoute};

		startRouteTransition(() => {
			if (routeChanged) {
				triggerScroll((prev) => !prev);
			}

			if (routeChanged || locationChanged) {
				setSlots((slots) =>
					navigateSlots(slots, {
						Component: isLocaleCorrect ? currentRoute?.component : PageNotFound,
						location,
						action: navigationAction,
						locale,
						keepAlive,
					}),
				);
			}

			if (locationChanged) {
				triggerRouteChange((prev) => !prev);
			}
		});
	}, [location, currentRoute, isLocaleCorrect]);

	const renderPage = (slot: PageSlot) => {
		const isCurrent = slot === slots.current;
		const Page = slot.Component;
		// Own location for a page hidden, shown again by the keep-alive, or being left for another page component
		// (it must not load the data of the next URL while it stays on screen); the router one for URL changes
		// within the page (filters, variants), as soon as they happen.
		const hasOwnLocation = !isCurrent || slot.isKeptAlive || incomingComponent !== slot.Component;

		return (
			<KeepAlive key={slot.id} visible={isCurrent} location={hasOwnLocation ? slot.location : undefined}>
				{/* A parked page is navigated away from: shell state it reads stays frozen (`useRoute`). */}
				<NavigationContext.Provider value={isCurrent ? isPending : true}>
					<ComponentErrorBoundary resetKey={`${slot.location.pathname}${slot.location.search}`}>
						<Page />
					</ComponentErrorBoundary>
				</NavigationContext.Provider>
			</KeepAlive>
		);
	};

	return (
		<ScrollRestoration triggerScroll={scroll} triggerChange={routeChanged} isPageKeptAlive={!!slots.current.isKeptAlive}>
			{!slots.current.Component ? (
				<LoaderComponent />
			) : (
				/*
				 * During navigation (startTransition + deferred location), a null
				 * fallback keeps the previously committed page visible instead of
				 * flashing the full-viewport LoaderComponent skeleton.
				 * One keyed list, oldest slot first: a page parked or shown again keeps its position (no remount).
				 */
				<Suspense fallback={isPending ? null : <LoaderComponent />}>
					{[slots.parked, slots.current].filter(Boolean).sort((a, b) => a.id - b.id).map(renderPage)}
				</Suspense>
			)}
			<Effects location={incomingLocation} onExternalRedirect={() => setExternalRedirectStatus(true)} />
		</ScrollRestoration>
	);
};

StrictLocaleLoaderRouter.shouldGetProps = () => false;

export default StrictLocaleLoaderRouter;
